import { getPostHog } from "./posthog";

export type AnalyticsParams = Record<string, unknown>;

export type TrackOptions = {
  /** Person properties set once (PostHog only), e.g. signup attribution. */
  setOnce?: AnalyticsParams;
  /** Person properties set on every call (PostHog only). */
  set?: AnalyticsParams;
};

/**
 * Minimal view of the authenticated user used to build analytics traits.
 * Mirrors `AuthData` from contexts/Auth but every field is optional so the
 * helpers can be fed partial objects (and stay easy to unit test).
 */
export type AnalyticsUser = {
  id?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  userHandle?: string;
  profilePicture?: string;
  premium?: boolean;
  premiumLifetime?: boolean;
  betaUser?: boolean;
  role?: number | boolean;
  partnerId?: number;
  partnerName?: string;
  subscriptionPlatform?: string | null;
  creditedUntil?: string | false | null;
};

export type AnalyticsPremiumState = {
  isPremium?: boolean;
  isReadOnly?: boolean;
};

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export const PLATFORM = "web";
export const APP_NAME = "redwing-web";

const sendToGtag = (eventName: string, params?: AnalyticsParams): void => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  window.gtag("event", eventName, params || {});
};

const sendToPostHog = (
  eventName: string,
  params?: AnalyticsParams,
  options?: TrackOptions
): void => {
  const client = getPostHog();
  if (!client) {
    return;
  }

  const properties: AnalyticsParams = { ...(params || {}) };
  if (options?.set) {
    properties.$set = options.set;
  }
  if (options?.setOnce) {
    properties.$set_once = options.setOnce;
  }

  client.capture(eventName, properties);
};

/**
 * Track a product event. Events are sent to PostHog (primary) and Google
 * Analytics (legacy). Use snake_case names shared with the mobile app so the
 * same event can be compared across platforms in PostHog.
 */
export const trackEvent = (
  eventName: string,
  params?: AnalyticsParams,
  options?: TrackOptions
): void => {
  sendToGtag(eventName, params);
  sendToPostHog(eventName, params, options);
};

export const trackPageView = (pagePath: string): void => {
  sendToGtag("page_view", {
    page_path: pagePath,
    page_location: window.location.href,
    page_title: document.title,
  });

  sendToPostHog("$pageview", {
    $current_url: window.location.href,
    path: pagePath,
    title: document.title,
  });
};

export const trackGoogleAdsConversion = (): void => {
  if (typeof window.gtag !== "function") {
    return;
  }

  window.gtag("event", "conversion", {
    send_to: "AW-10814814067/g_sTCImJoYYDEPPu86Qo",
  });
};

/**
 * Sign-up conversion. Stamps first-touch attribution on the person so
 * "which platform did this user sign up on" is a property, not a query.
 */
export const trackSignUp = (method: string): void => {
  trackEvent(
    "sign_up",
    { method },
    {
      setOnce: {
        signup_platform: PLATFORM,
        signup_method: method,
        signup_date: new Date().toISOString(),
      },
    }
  );
};

const hasActiveCredit = (creditedUntil?: string | false | null): boolean => {
  if (!creditedUntil) {
    return false;
  }
  const expiresAt = new Date(creditedUntil).getTime();
  return Number.isFinite(expiresAt) && expiresAt > Date.now();
};

export const getPremiumType = (
  user: AnalyticsUser | undefined,
  premium: AnalyticsPremiumState = {}
): string => {
  if (!user) {
    return "free";
  }
  if (user.betaUser) {
    return "beta_user";
  }
  if (user.premiumLifetime) {
    return "lifetime";
  }
  if (hasActiveCredit(user.creditedUntil)) {
    return "credit";
  }
  if (premium.isPremium || user.premium) {
    return "subscription";
  }
  return "free";
};

const getUserRole = (user?: AnalyticsUser): string => {
  if (!user) {
    return "anonymous";
  }
  if (user.role) {
    return "admin";
  }
  if (user.partnerId) {
    return "partner";
  }
  return "user";
};

/** Person properties stored on the PostHog person profile. */
export const buildUserTraits = (
  user: AnalyticsUser,
  premium: AnalyticsPremiumState = {}
): AnalyticsParams => {
  const isPremium = Boolean(
    premium.isPremium || user.premium || user.premiumLifetime || user.betaUser
  );

  return {
    email: user.email,
    first_name: user.firstName,
    last_name: user.lastName,
    name:
      [user.firstName, user.lastName].filter(Boolean).join(" ") || undefined,
    user_handle: user.userHandle,
    has_profile_picture: Boolean(user.profilePicture),
    is_premium: isPremium,
    premium_type: getPremiumType(user, premium),
    premium_lifetime: Boolean(user.premiumLifetime),
    beta_user: Boolean(user.betaUser),
    subscription_platform: user.subscriptionPlatform || null,
    paywall_read_only: Boolean(premium.isReadOnly),
    role: getUserRole(user),
    is_partner: Boolean(user.partnerId),
    partner_id: user.partnerId || null,
    partner_name: user.partnerName || null,
    last_seen_platform: PLATFORM,
  };
};

/**
 * Super properties attached to every event. These are what make "web vs
 * mobile" and "free vs premium" breakdowns a single filter in PostHog.
 */
export const buildSuperProperties = (
  user: AnalyticsUser | undefined,
  premium: AnalyticsPremiumState = {}
): AnalyticsParams => ({
  platform: PLATFORM,
  app: APP_NAME,
  is_authenticated: Boolean(user?.id),
  is_premium: Boolean(
    user &&
      (premium.isPremium ||
        user.premium ||
        user.premiumLifetime ||
        user.betaUser)
  ),
  premium_type: getPremiumType(user, premium),
  subscription_platform: user?.subscriptionPlatform || null,
  user_role: getUserRole(user),
  paywall_read_only: Boolean(user && premium.isReadOnly),
});

export const registerSuperProperties = (properties: AnalyticsParams): void => {
  getPostHog()?.register(properties);
};

export const identifyUser = (
  user: AnalyticsUser,
  premium: AnalyticsPremiumState = {}
): void => {
  const client = getPostHog();
  if (!client || !user.id) {
    return;
  }

  client.identify(`${user.id}`, buildUserTraits(user, premium), {
    first_seen_platform: PLATFORM,
  });
};

export const resetAnalyticsUser = (): void => {
  getPostHog()?.reset();
};
